//#region imports
import React, { useState, useEffect, useRef } from 'react';

// bootstrap
import { Container, Row, Col, Card, Button, Form, Alert } from 'react-bootstrap';

// redux
import { useDispatch } from 'react-redux';
import { confirmMfaCompleted } from '_redux/slices/profile/staff-management';

// firebase
import {
  getAuth,
  multiFactor,
  TotpMultiFactorGenerator,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
} from 'firebase/auth';

// qr code (rendered locally, the TOTP secret never leaves the browser)
import QRCode from 'qrcode';

// styling
import { Colors } from '_styles';

// components
import LogoutButton from '_components/atoms/button/logout-button';

//#endregion

// steps: 'choose' | 'totp-verify' | 'sms-phone' | 'sms-verify' | 'complete'
const MfaSetup = () => {

  //#region local states
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', variant: 'danger' });
  const [step, setStep] = useState('choose');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  //#endregion

  // non-serializable firebase objects kept out of react state / redux
  const totpSecretRef = useRef(null);
  const verificationIdRef = useRef(null);
  const recaptchaRef = useRef(null);

  // firebase auth (modular - required for multi-factor APIs)
  const auth = getAuth();
  const currentUser = auth.currentUser;

  // redux
  const dispatch = useDispatch();

  //#region setup flows

  // start TOTP (authenticator app) enrollment
  const handleStartTotp = async () => {
    if (!currentUser) {
      setAlert({ show: true, message: 'No authenticated user found.', variant: 'danger' });
      return;
    }

    setLoading(true);

    try {
      const multiFactorSession = await multiFactor(currentUser).getSession();
      const totpSecret = await TotpMultiFactorGenerator.generateSecret(multiFactorSession);
      totpSecretRef.current = totpSecret;

      const totpUri = totpSecret.generateQrCodeUrl(currentUser.email, 'PRM Assist');

      // render the QR code locally
      const qrImageUrl = await QRCode.toDataURL(totpUri, { width: 200, margin: 1 });
      setQrCodeUrl(qrImageUrl);
      setSecretKey(totpSecret.secretKey);
      setVerificationCode('');
      setStep('totp-verify');
    }
    catch (error) {
      console.error('Error setting up authenticator app:', error);

      let errorMessage = 'Failed to start setup. Please try again.';
      if (error.code === 'auth/requires-recent-login') {
        errorMessage = 'Please log out and log back in before setting up MFA.';
      } else if (error.code === 'auth/unverified-email') {
        errorMessage = 'Please verify your email before setting up MFA.';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many requests. Please wait before trying again.';
      }

      setAlert({ show: true, message: errorMessage, variant: 'danger' });
    }
    finally {
      setLoading(false);
    }
  };

  // verify the TOTP code and enroll
  const handleVerifyTotp = async (e) => {
    e.preventDefault();

    if (!verificationCode || verificationCode.length !== 6) {
      setAlert({ show: true, message: 'Please enter a valid 6-digit code.', variant: 'danger' });
      return;
    }

    if (!totpSecretRef.current) {
      setAlert({ show: true, message: 'Setup not properly initialized. Please restart the process.', variant: 'danger' });
      return;
    }

    setLoading(true);

    try {
      const multiFactorAssertion = TotpMultiFactorGenerator.assertionForEnrollment(
        totpSecretRef.current,
        verificationCode
      );

      await multiFactor(currentUser).enroll(multiFactorAssertion, 'Authenticator app');
      await handleEnrollmentComplete();
    }
    catch (error) {
      console.error('Error verifying code:', error);
      setAlert({ show: true, message: getVerifyErrorMessage(error), variant: 'danger' });
      setLoading(false);
    }
  };

  // send the SMS verification code
  const handleSendSmsCode = async (e) => {
    e.preventDefault();

    if (!currentUser) {
      setAlert({ show: true, message: 'No authenticated user found.', variant: 'danger' });
      return;
    }

    if (!phoneNumber || phoneNumber.replace(/\D/g, '').length < 7) {
      setAlert({ show: true, message: 'Please enter a valid phone number including country code (e.g. +353871234567).', variant: 'danger' });
      return;
    }

    setLoading(true);

    try {
      // recaptcha is required by firebase for phone enrollment
      // firebase@9.23 signature: RecaptchaVerifier(containerOrId, parameters, auth)
      if (!recaptchaRef.current) {
        recaptchaRef.current = new RecaptchaVerifier('mfa-recaptcha-container', { size: 'invisible' }, auth);
      }

      const multiFactorSession = await multiFactor(currentUser).getSession();
      const phoneAuthProvider = new PhoneAuthProvider(auth);

      verificationIdRef.current = await phoneAuthProvider.verifyPhoneNumber(
        { phoneNumber: phoneNumber.trim(), session: multiFactorSession },
        recaptchaRef.current
      );

      setVerificationCode('');
      setStep('sms-verify');
      setAlert({ show: true, message: `Verification code sent to ${phoneNumber}`, variant: 'success' });
    }
    catch (error) {
      console.error('Error sending SMS code:', error);

      // reset recaptcha so the user can retry
      try {
        recaptchaRef.current?.clear();
      } catch (clearError) { /* ignore */ }
      recaptchaRef.current = null;
      const container = document.getElementById('mfa-recaptcha-container');
      if (container) container.innerHTML = '';

      let errorMessage = 'Failed to send verification code. Please try again.';
      if (error.code === 'auth/invalid-phone-number') {
        errorMessage = 'Invalid phone number. Include the country code (e.g. +353871234567).';
      } else if (error.code === 'auth/invalid-app-credential') {
        // Firebase blocks phone verification from "localhost"; 127.0.0.1 or a deployed domain works
        const host = window.location.hostname;
        errorMessage = (host === 'localhost' || host === '127.0.0.1')
          ? 'SMS verification is blocked on localhost. Open the app via 127.0.0.1 (and add it under Firebase Auth → Authorized domains), or use an authenticator app.'
          : 'Phone verification failed. Confirm this domain is authorized in Firebase Auth, then refresh and try again.';
      } else if (error.code === 'auth/unverified-email') {
        errorMessage = 'Please verify your email before setting up MFA.';
      } else if (error.code === 'auth/requires-recent-login') {
        errorMessage = 'Please log out and log back in before setting up MFA.';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many requests. Please wait before trying again.';
      } else if (error.code === 'auth/operation-not-allowed') {
        errorMessage = 'SMS verification is not enabled. Please use an authenticator app instead.';
      }

      setAlert({ show: true, message: errorMessage, variant: 'danger' });
    }
    finally {
      setLoading(false);
    }
  };

  // verify the SMS code and enroll
  const handleVerifySms = async (e) => {
    e.preventDefault();

    if (!verificationCode || verificationCode.length !== 6) {
      setAlert({ show: true, message: 'Please enter a valid 6-digit code.', variant: 'danger' });
      return;
    }

    if (!verificationIdRef.current) {
      setAlert({ show: true, message: 'Setup not properly initialized. Please restart the process.', variant: 'danger' });
      return;
    }

    setLoading(true);

    try {
      const phoneCredential = PhoneAuthProvider.credential(verificationIdRef.current, verificationCode);
      const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(phoneCredential);

      await multiFactor(currentUser).enroll(multiFactorAssertion, 'SMS');
      await handleEnrollmentComplete();
    }
    catch (error) {
      console.error('Error verifying code:', error);
      setAlert({ show: true, message: getVerifyErrorMessage(error), variant: 'danger' });
      setLoading(false);
    }
  };

  // shared completion: tell the backend (verified server-side) and refresh
  const handleEnrollmentComplete = async () => {
    try {
      // backend verifies enrollment via the admin SDK before updating claims
      await dispatch(confirmMfaCompleted()).unwrap();
    } catch (confirmError) {
      // enrollment succeeded even if confirmation failed, the gate checks enrolled factors
      console.error('Error confirming MFA completion:', confirmError);
    }

    // refresh token so new claims are picked up
    await currentUser.getIdToken(true);

    setStep('complete');
    setLoading(false);
  };

  const getVerifyErrorMessage = (error) => {
    if (error.code === 'auth/invalid-verification-code') {
      return 'Invalid verification code. Please try again.';
    }
    if (error.code === 'auth/code-expired') {
      return 'Verification code expired. Please restart the setup.';
    }
    return 'Invalid verification code. Please try again.';
  };

  //#endregion

  // function to handle logout error
  const handleLogoutError = () => {
    setAlert({ show: true, message: 'Error during logout. Please try again.', variant: 'danger' });
  };

  // show alert for 5 seconds
  useEffect(() => {
    if (alert.show) {
      const timer = setTimeout(() => {
        setAlert({ show: false, message: '', variant: 'danger' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [alert.show]);

  const getStepTitle = () => {
    switch (step) {
      case 'choose':
        return 'Multi-Factor Authentication';
      case 'totp-verify':
        return 'Scan the QR Code';
      case 'sms-phone':
        return 'SMS Verification';
      case 'sms-verify':
        return 'Enter Verification Code';
      case 'complete':
        return 'MFA Setup Complete';
      default:
        return 'Multi-Factor Authentication';
    }
  };

  const getStepDescription = () => {
    switch (step) {
      case 'choose':
        return 'Choose how you want to secure your account';
      case 'totp-verify':
        return 'Scan the QR code with your authenticator app, then enter the 6-digit code';
      case 'sms-phone':
        return 'Enter your phone number to receive verification codes by SMS';
      case 'sms-verify':
        return 'Enter the 6-digit code sent to your phone';
      case 'complete':
        return 'Multi-factor authentication has been enabled for your account';
      default:
        return '';
    }
  };

  const codeInput = (onSubmit, buttonLabel) => (
    <Form onSubmit={onSubmit}>
      <Form.Group className="mb-3">
        <Form.Label>Enter the 6-digit code</Form.Label>
        <Form.Control
          type="text"
          placeholder="000000"
          value={verificationCode}
          onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
          maxLength={6}
          required
          disabled={loading}
          className="text-center"
          style={{ fontSize: '1.2em', letterSpacing: '0.2em' }}
        />
      </Form.Group>

      <Button
        type="submit"
        className="w-100 mb-3"
        style={{ backgroundColor: Colors.PRM_LIGHT_BLUE, borderColor: Colors.PRM_LIGHT_BLUE }}
        disabled={loading}
      >
        {loading ? 'Verifying...' : buttonLabel}
      </Button>

      <div className="text-center">
        <Button
          variant="link"
          onClick={() => setStep('choose')}
          disabled={loading}
          className="p-0"
        >
          Choose a different method
        </Button>
      </div>
    </Form>
  );

  return (
    <Container fluid className="d-flex align-items-center justify-content-center min-vh-100" style={{ backgroundColor: '#f8f9fa' }}>
      <Row className="w-100 justify-content-center">
        <Col xs={12} md={8} lg={6} xl={4}>
          <Card className="shadow-sm">
            <Card.Body className="p-5">

              {/* title */}
              <div className="text-center mb-4">
                <h2 style={{ color: Colors.PRM_LIGHT_BLUE, fontWeight: 'bold' }}>
                  {getStepTitle()}
                </h2>
                <p className="text-muted">
                  {getStepDescription()}
                </p>
              </div>

              {alert.show && (
                <Alert variant={alert.variant} className="mb-3">
                  {alert.message}
                </Alert>
              )}

              {currentUser && step !== 'complete' && (
                <div className="text-center mb-4">
                  <p className="text-muted">
                    Account: <strong>{currentUser.email}</strong>
                  </p>
                </div>
              )}

              {step === 'choose' && (
                <div>
                  <Button
                    onClick={handleStartTotp}
                    className="w-100 mb-3"
                    style={{ backgroundColor: Colors.PRM_LIGHT_BLUE, borderColor: Colors.PRM_LIGHT_BLUE }}
                    disabled={loading}
                  >
                    {loading ? 'Setting up...' : 'Use an Authenticator App (Recommended)'}
                  </Button>

                  <Button
                    onClick={() => { setAlert({ show: false, message: '', variant: 'danger' }); setStep('sms-phone'); }}
                    variant="outline-primary"
                    className="w-100 mb-3"
                    disabled={loading}
                  >
                    Use SMS Text Messages
                  </Button>

                  <p className="small text-muted text-center">
                    An authenticator app such as Google Authenticator or Authy is the most secure option.
                  </p>
                </div>
              )}

              {step === 'totp-verify' && (
                <div>
                  <div
                    className="mb-4 p-3 rounded"
                    style={{ backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0' }}
                  >
                    <p className="fw-semibold mb-2" style={{ color: Colors.PRM_LIGHT_BLUE }}>
                      Don't have Google Authenticator yet?
                    </p>
                    <ol className="small text-muted mb-2 ps-3">
                      <li className="mb-1">
                        On your phone, open the{' '}
                        <strong>App Store</strong> (iPhone) or{' '}
                        <strong>Google Play Store</strong> (Android).
                      </li>
                      <li className="mb-1">
                        Search for <strong>Google Authenticator</strong> and install it
                        (made by Google LLC).
                      </li>
                      <li className="mb-1">
                        Open the app, tap <strong>+</strong> or <strong>Scan a QR code</strong>,
                        then point your camera at the QR code below.
                      </li>
                      <li>
                        Enter the 6-digit code shown in the app to finish setup.
                      </li>
                    </ol>
                    <p className="small mb-0">
                      <a
                        href="https://apps.apple.com/app/google-authenticator/id388497605"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Download for iPhone
                      </a>
                      {' · '}
                      <a
                        href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Download for Android
                      </a>
                    </p>
                  </div>

                  <div className="text-center mb-4">
                    <img src={qrCodeUrl} alt="MFA QR Code" className="img-fluid mb-3" />
                    <p className="small text-muted">
                      Can't scan? Enter this code manually in your app:
                      <br />
                      <code>{secretKey}</code>
                    </p>
                  </div>

                  {codeInput(handleVerifyTotp, 'Verify Code')}
                </div>
              )}

              {step === 'sms-phone' && (
                <Form onSubmit={handleSendSmsCode}>
                  <Form.Group className="mb-3">
                    <Form.Label>Phone Number</Form.Label>
                    <Form.Control
                      type="tel"
                      placeholder="+353871234567"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      required
                      disabled={loading}
                    />
                    <Form.Text className="text-muted">
                      Include your country code (e.g. +353 for Ireland)
                    </Form.Text>
                  </Form.Group>

                  <Button
                    type="submit"
                    className="w-100 mb-3"
                    style={{ backgroundColor: Colors.PRM_LIGHT_BLUE, borderColor: Colors.PRM_LIGHT_BLUE }}
                    disabled={loading}
                  >
                    {loading ? 'Sending...' : 'Send Verification Code'}
                  </Button>

                  <div className="text-center">
                    <Button
                      variant="link"
                      onClick={() => setStep('choose')}
                      disabled={loading}
                      className="p-0"
                    >
                      Choose a different method
                    </Button>
                  </div>
                </Form>
              )}

              {step === 'sms-verify' && codeInput(handleVerifySms, 'Verify Code')}

              {step === 'complete' && (
                <div>
                  <Alert variant="success" className="mb-4">
                    <strong>Success!</strong> Multi-factor authentication has been enabled for your account.
                    From now on, you'll need to enter a verification code when logging in.
                  </Alert>

                  <Button
                    onClick={() => window.location.reload()}
                    className="w-100 mb-3"
                    style={{ backgroundColor: Colors.PRM_LIGHT_BLUE, borderColor: Colors.PRM_LIGHT_BLUE }}
                  >
                    Continue to Portal
                  </Button>
                </div>
              )}

              {/* invisible recaptcha anchor for SMS enrollment */}
              <div id="mfa-recaptcha-container" />

              {/* Logout Button */}
              {step !== 'complete' && (
                <div className="text-center mt-3">
                  <LogoutButton
                    disabled={loading}
                    onLogoutError={handleLogoutError}
                  />
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default MfaSetup;
